import React, { useEffect, useState } from 'react';
import { Tabs, Table, Button, Input, message, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import { useTheme } from '../contexts/ThemeContext';
import { agentConfigApi } from '../services/api-services';

interface EnvItem {
  key: string;
  value: string;
}

const HermesEnvTab: React.FC = () => {
  const [items, setItems] = useState<EnvItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEnv = async () => {
    setLoading(true);
    try {
      const res: any = await agentConfigApi.getHermesEnv();
      setItems(res.items || []);
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEnv(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await agentConfigApi.updateHermesEnv(items);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setItems([...items, { key: '', value: '' }]);
  };

  const handleDelete = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'key' | 'value', val: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: val };
    setItems(newItems);
  };

  const columns = [
    {
      title: '变量名',
      dataIndex: 'key',
      width: '35%',
      render: (_: string, __: EnvItem, index: number) => (
        <Input
          value={items[index].key}
          onChange={(e) => handleChange(index, 'key', e.target.value)}
          placeholder="KEY"
          style={{ fontFamily: 'monospace' }}
        />
      ),
    },
    {
      title: '值',
      dataIndex: 'value',
      render: (_: string, __: EnvItem, index: number) => (
        <Input
          value={items[index].value}
          onChange={(e) => handleChange(index, 'value', e.target.value)}
          placeholder="VALUE"
          style={{ fontFamily: 'monospace' }}
        />
      ),
    },
    {
      title: '操作',
      width: 80,
      render: (_: any, __: EnvItem, index: number) => (
        <Popconfirm title="确认删除？" onConfirm={() => handleDelete(index)}>
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>保存</Button>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        rowKey={(_, index) => String(index)}
        pagination={false}
        loading={loading}
        size="middle"
      />
      <Button icon={<PlusOutlined />} onClick={handleAdd} style={{ marginTop: 12 }}>添加变量</Button>
    </div>
  );
};

const ClaudeSettingsTab: React.FC = () => {
  const [content, setContent] = useState('');
  const [envItems, setEnvItems] = useState<EnvItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res: any = await agentConfigApi.getClaudeSettings();
      const text = res.content || '{}';
      const extras: Record<string, string> = res.extras || {};
      setContent(text);
      try {
        const parsed = JSON.parse(text);
        const env = parsed.env || {};
        const merged = { ...extras, ...env };
        setEnvItems(Object.entries(merged).map(([key, value]) => ({ key, value: String(value) })));
      } catch {}
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleFormSave = async () => {
    setSaving(true);
    try {
      const parsed = JSON.parse(content);
      parsed.env = Object.fromEntries(envItems.filter(i => i.key).map(i => [i.key, i.value]));
      const text = JSON.stringify(parsed, null, 2);
      await agentConfigApi.updateClaudeSettings(text);
      setContent(text);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => setEnvItems([...envItems, { key: '', value: '' }]);
  const handleDelete = (i: number) => setEnvItems(envItems.filter((_, idx) => idx !== i));
  const handleChange = (i: number, field: 'key' | 'value', val: string) => {
    const next = [...envItems];
    next[i] = { ...next[i], [field]: val };
    setEnvItems(next);
  };

  if (editing) {
    return (
      <FileEditorTab
        load={async () => content}
        save={async (text) => { await agentConfigApi.updateClaudeSettings(text); setContent(text); setEditing(false); loadSettings(); }}
        language="json"
        filePath="~/.claude/settings.json"
        onCancel={() => setEditing(false)}
      />
    );
  }

  const columns = [
    {
      title: '变量名', dataIndex: 'key', width: '35%',
      render: (_: string, __: EnvItem, i: number) => (
        <Input value={envItems[i].key} onChange={(e) => handleChange(i, 'key', e.target.value)} placeholder="KEY" style={{ fontFamily: 'monospace' }} />
      ),
    },
    {
      title: '值', dataIndex: 'value',
      render: (_: string, __: EnvItem, i: number) => (
        <Input value={envItems[i].value} onChange={(e) => handleChange(i, 'value', e.target.value)} placeholder="VALUE" style={{ fontFamily: 'monospace' }} />
      ),
    },
    {
      title: '操作', width: 80,
      render: (_: any, __: EnvItem, i: number) => (
        <Popconfirm title="确认删除？" onConfirm={() => handleDelete(i)}>
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={() => setEditing(true)}>JSON编辑</Button>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleFormSave} loading={saving}>保存</Button>
      </div>
      <Table
        dataSource={envItems}
        columns={columns}
        rowKey={(_, i) => String(i)}
        pagination={false}
        loading={loading}
        size="middle"
        title={() => <strong>env</strong>}
      />
      <Button icon={<PlusOutlined />} onClick={handleAdd} style={{ marginTop: 12 }}>添加变量</Button>
    </div>
  );
};

interface FileEditorTabProps {
  load: () => Promise<string>;
  save: (content: string) => Promise<any>;
  language: string;
  filePath: string;
  onCancel?: () => void;
}

const FileEditorTab: React.FC<FileEditorTabProps> = ({ load, save, language, filePath, onCancel }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    setLoading(true);
    load().then(setContent).catch(() => message.error('加载失败')).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await save(content);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {onCancel && <Button onClick={onCancel}>取消</Button>}
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>保存</Button>
      </div>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
        <Editor
          height="500px"
          language={language}
          theme={theme === 'glass' ? 'vs-dark' : 'light'}
          value={content}
          onChange={(val) => setContent(val || '')}
          options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, padding: { top: 12 } }}
          loading={loading ? '加载中...' : undefined}
        />
      </div>
      <div style={{ marginTop: 8, color: 'var(--muted-text)', fontSize: 12 }}>文件路径：{filePath}</div>
    </div>
  );
};

const HermesConfigTab: React.FC = () => {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [parsed, setParsed] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res: any = await agentConfigApi.getHermesConfig();
      const text = res.content || '';
      setContent(text);
      try {
        const lines = text.split('\n');
        const model: Record<string, string> = {};
        const commandAllowlist: string[] = [];
        let section = '';
        for (const line of lines) {
          if (line.startsWith('model:')) { section = 'model'; continue; }
          if (line.startsWith('command_allowlist:')) { section = 'command_allowlist'; continue; }
          if (/^\S/.test(line) && !line.startsWith('- ') && section) { section = ''; continue; }
          if (section === 'model' && line.startsWith('  ')) {
            const m = line.match(/^\s+(\w+):\s*(.*)$/);
            if (m) model[m[1]] = m[2];
          }
          if (section === 'command_allowlist' && line.startsWith('- ')) {
            commandAllowlist.push(line.replace(/^- /, ''));
          }
        }
        setParsed({ model, commandAllowlist, _origModel: { ...model } });
      } catch {}
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleFormSave = async () => {
    let text = content;
    const origModel = parsed._origModel || {};
    for (const [k, v] of Object.entries(parsed.model || {})) {
      if (origModel[k] !== v) {
        text = text.replace(new RegExp(`(^  ${k}:\\s*)(.*)$`, 'm'), `$1${v}`);
      }
    }
    const oldBlock = content.match(/command_allowlist:\n((?:- .+\n?)*)/);
    if (oldBlock) {
      const newBlock = 'command_allowlist:\n' + (parsed.commandAllowlist || []).filter((s: string) => s.trim()).map((s: string) => `- ${s}`).join('\n') + '\n';
      text = text.replace(oldBlock[0], newBlock);
    }
    setContent(text);
    setSaving(true);
    try {
      await agentConfigApi.updateHermesConfig(text);
      message.success('保存成功');
      loadConfig();
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <FileEditorTab
        load={async () => content}
        save={async (text) => { await agentConfigApi.updateHermesConfig(text); setContent(text); setEditing(false); loadConfig(); }}
        language="yaml"
        filePath="~/.hermes/config.yaml"
        onCancel={() => setEditing(false)}
      />
    );
  }

  const handleModelChange = (field: string, val: string) => {
    setParsed((prev: any) => ({ ...prev, model: { ...prev.model, [field]: val } }));
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={() => setEditing(true)}>YAML编辑</Button>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleFormSave} loading={saving}>保存</Button>
      </div>
      <Table
        dataSource={Object.entries(parsed.model || {}).map(([k, v]) => ({ key: k, field: k, value: v as string }))}
        columns={[
          { title: '字段', dataIndex: 'field', width: '30%' },
          { title: '值', dataIndex: 'value', render: (val: string, record: any) => (
            <Input value={val} onChange={(e) => handleModelChange(record.field, e.target.value)} style={{ fontFamily: 'monospace' }} />
          )},
        ]}
        pagination={false}
        size="middle"
        title={() => <strong>Model</strong>}
        loading={loading}
        style={{ marginBottom: 24 }}
      />
      <div>
        <strong style={{ display: 'block', marginBottom: 8 }}>Command Allowlist</strong>
        {(parsed.commandAllowlist || []).map((cmd: string, i: number) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Input
              value={cmd}
              onChange={(e) => {
                const next = [...(parsed.commandAllowlist || [])];
                next[i] = e.target.value;
                setParsed((prev: any) => ({ ...prev, commandAllowlist: next }));
              }}
              style={{ fontFamily: 'monospace' }}
              placeholder="命令"
            />
            <Popconfirm title="确认删除？" onConfirm={() => setParsed((prev: any) => ({ ...prev, commandAllowlist: prev.commandAllowlist.filter((_: any, idx: number) => idx !== i) }))}>
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        ))}
        <Button icon={<PlusOutlined />} onClick={() => setParsed((prev: any) => ({ ...prev, commandAllowlist: [...(prev.commandAllowlist || []), ''] }))}>添加命令</Button>
      </div>
    </div>
  );
};

const AgentConfig: React.FC = () => {
  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">Agent配置</h1>
      </div>
      <p className="apple-page-subtitle">对智能体的配置管理</p>
      <Tabs
        defaultActiveKey="env"
        items={[
          { key: 'env', label: '.env', children: <HermesEnvTab /> },
          { key: 'config', label: 'config.yaml', children: <HermesConfigTab /> },
        ]}
      />
    </div>
  );
};

export default AgentConfig;
